import { Icon } from 'semantic-ui-react';
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { StripMessageWrapper, DropdownWrapper, NavbarWrapper } from './styled';
import useActivePage from '../../../../r/shared/custom-hooks/active-page';
import { files } from '../../../libs/assets';
import win from '../../../../r/shared/win';
import Button from '../button';
import Link from '../link';

const applicablePages = ['Features'];

const navigation = [
  {
    text: 'Solution',
    target: '_self',
    dropdown: [
      {
        items: [
          { href: '/survey-forms', text: 'Online Surveys' },
          { href: '/data-collection', text: 'Data Collection' },
          { href: '/registration-forms', text: 'Registration Form' },
          { href: '/application-forms', text: 'Application Form' },
          { href: '/e-signature-forms', text: 'E-Signature' },
          { href: '/booking-forms', text: 'Booking/Reservations' }
        ]
      },
      {
        items: [
          { href: '/payment-forms', text: 'Payment Forms' },
          { href: '/order-forms', text: 'Order Forms' },
          { href: '/offline-forms', text: 'Offline Forms' },
          { href: '/contact-forms', text: 'Contact Forms' },
          { href: '/sign-up-forms', text: 'Sign-Up Forms' }
        ]
      }
    ]
  },
  { href: '/templates', text: 'Templates', target: '_self' },
  {
    to: '/features',
    text: 'Features',
    target: '_self',
    dropdown: [
      {
        title: 'Security',
        items: [
          { to: '/features/gdpr', text: 'GDPR Compliant' },
          { to: '/features/private-forms', text: 'Private Forms' },
          { href: '/payments', text: 'PCI Complaint' },
          { to: '/features/subdomain', text: 'Subdomain' },
          { to: '/features/secure-forms', text: 'Secure Form' }
        ]
      },
      {
        title: 'Form Options',
        items: [
          { to: '/features/electronic-signatures', text: 'Digital signature' },
          { to: '/features/mobile-forms', text: 'Mobile Forms' },
          { to: '/features/geolocation', text: 'Location Enabled Forms' },
          { to: '/features/multi-page-forms', text: 'Multi-Page Form' },
          { to: '/features/form-customisation', text: 'Customization' }
        ]
      },
      {
        title: 'Integrations',
        items: [
          { to: '/features/sheet-integration', text: 'Google Sheet Integrations' },
          { href: '/payments', text: 'Payment' },
          { to: '/features/cloud-storage', text: 'Cloud Storage' },
          { to: '/features/zapier-integration', text: 'Zapier Integration' }
        ]
      },
      {
        title: 'Teams & Analytics',
        items: [
          { to: '/features/multiple-users', text: 'Multiple users and Teams' },
          { to: '/features/analytics', text: 'Form Analytics' },
          { to: '/features/data-import-export', text: 'Data Import & Export' }
        ]
      },
      {
        title: 'Sharing Options',
        items: [
          { to: '/features/embed-form-in-website', text: 'Embeddable Forms' },
          { to: '/features/customized-form-links', text: 'Custom Form Link' },
          { href: 'https://help.formpl.us/s/article/a-guide-to-embedding-your-forms-on-wordpress', text: 'Wordpress Form Embed' }
        ]
      },
      {
        title: 'Notifications',
        items: [
          { to: '/features/track-email-invitation', text: 'Email Invitation' },
          { to: '/features/email-confirmations', text: 'Confirmation Emails' },
          { to: '/features/email-notifications', text: 'Notify Teammates' },
          { to: '/features/email-notifications', text: 'Periodic Notification' },
          { to: '/features/document-merge', text: 'Document Merge' }
        ]
      }
    ]
  },
  {
    text: 'Resources',
    target: '_self',
    dropdown: [
      {
        items: [
          { href: '/blog', content: 'Information, how-to and guides to data collection and surveys.', text: 'Blog' },
          { href: 'https://help.formpl.us', content: 'All the help you need on using Formplus', text: 'Support' }
        ]
      }
    ]
  },
  { href: '/pricing', target: '_self', text: 'Pricing' }
];

const StripMessage = ({ text_color, content, link = '', color, cta }) => {
  const style = { color: text_color, backgroundColor: color };

  return (
    <StripMessageWrapper style={style} title="Click here to view details">
      <div className="message">{content}</div>
      {link && <Button size="small" href={link} borderless variant="primary" text={cta} />}
    </StripMessageWrapper>
  );
};

const SubMenu = ({ list, title: { target, text, href, to } }) => {
  return (
    <DropdownWrapper className="dropdown">
      <div className="title">
        {(href || to) ? <Link target={target} href={href} to={to}>{text}</Link> : text}
        <Icon className="icon chevron down" />
      </div>

      <div className={`menu ${text.toLowerCase()}`}>
        {list.map(({ title, items }, dropdownIndex) => (
          <div className="item" key={dropdownIndex}>
            {title && <h3>{title}</h3>}
            {items.map(({ content, href, text, to }) => (
              <div className="sub-item" key={text}>
                <Link href={href} to={to}>{text}</Link>
                {content && <p>{content}</p>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </DropdownWrapper>
  );
};

// If you are working on a page (e.g.: /data-collection page) where the navbar requires
// any other color than white, just add the color against the page pathname.
const navbarThemeColors = {
  pathname: 'color'
};

const Navbar = () => {
  const navbarTheme = navbarThemeColors[location.pathname] ?? 'white';
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const { stripMessage, isLoggedIn } = win;
  const activePage = useActivePage();

  const handleMobileMenuToggle = () => setOpenMobileMenu(!openMobileMenu);

  if (!applicablePages.includes(activePage.name)) return null;

  return (
    <>
      {stripMessage && <StripMessage {...stripMessage} />}
      <NavbarWrapper style={{ '--theme-color': navbarTheme }}>
        <div className="link navbar-toggler" onClick={handleMobileMenuToggle}>
          <button aria-label="Toggle navigation" data-toggle="collapse" type="button">
            <svg width="24" height="24" clipRule="evenodd" fillRule="evenodd" strokeLinejoin="round" strokeMiterlimit="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="m22 16.75c0-.414-.336-.75-.75-.75h-18.5c-.414 0-.75.336-.75.75s.336.75.75.75h18.5c.414 0 .75-.336.75-.75zm0-5c0-.414-.336-.75-.75-.75h-18.5c-.414 0-.75.336-.75.75s.336.75.75.75h18.5c.414 0 .75-.336.75-.75zm0-5c0-.414-.336-.75-.75-.75h-18.5c-.414 0-.75.336-.75.75s.336.75.75.75h18.5c.414 0 .75-.336.75-.75z" fillRule="nonzero"/>
            </svg>
          </button>
        </div>

        <div className="link brand">
          <a href="/">
            <img src={files.logo} alt="FormPlus Logo" />
          </a>
        </div>

        <nav className={`${openMobileMenu ? 'expand' : ''} right`}>
          <div className="link">
            <a href="/">Home</a>
          </div>

          {navigation.map(({ dropdown, target, href = '', text, to = '' }) => (
            <div className="link" key={text}>
              {(!href && !to) ? (
                <SubMenu title={{ target, text, href, to }} list={dropdown} />
              ) : (
                !dropdown ? (
                  <Link target={target} href={href} to={to}>{text}</Link>
                ) : (
                  <SubMenu title={{ target, href, text, to }} list={dropdown} />
                )
              )}
            </div>
          ))}

          <div className="link auth">
            {!isLoggedIn ? (
              <>
                <Button href="/auth?signin=true" borderless className="secondary shadow" text="Log in" />
                <Button href="/auth" borderless variant="primary" text="Sign up" />
              </>
            ) : (
              <Button href="/r" borderless className="secondary shadow" text="Dashboard" />
            )}
          </div>
        </nav>

        <nav className="right mobile">
          <div className="link">
            <Button href={!isLoggedIn ? '/auth?signin=true' : '/r'}
              text={!isLoggedIn ? 'Log in' : 'Dashboard'}
              className="secondary shadow"
              borderless
            />
          </div>
        </nav>
      </NavbarWrapper>
    </>
  );
};

SubMenu.propTypes = {
  title: PropTypes.shape({
    target: PropTypes.oneOf(['_parent', '_blank', '_self', '_top']),
    text: PropTypes.string,
    href: PropTypes.string,
    to: PropTypes.string
  }).isRequired,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          content: PropTypes.string,
          text: PropTypes.string,
          href: PropTypes.string,
          to: PropTypes.string,
        })
      )
    })
  ).isRequired
};

export default Navbar;
